import { useMemo } from 'react';
import { Container } from '@geberit/gdds';

// styles
import styles from './button-group.module.scss';
import ctaButtonStyles from '../call-to-action/cta-button.module.scss';

// components
import { CTAButton } from '../call-to-action/cta-button';
import { SectionClosingLine } from '../Common/section-closing-line';

// utils
import { decodingContent } from 'utils/decodingContent';
import { classNameBuilder } from 'utils/classNameBuilder';
import { buildSize, gridSizes } from 'utils/gridSize';

type ButtonProps = (Link & { showArrow: boolean }) | null;

export type ButtonGroupProps = {
  border: string;
  contentIndex: number;
  contentLength: number;
  previewId: string;
  primaryLink: ButtonProps;
  firstSecondaryLink: ButtonProps;
  secondSecondaryLink: ButtonProps;
  thirdSecondaryLink: ButtonProps;
  isChild: boolean;
  isContainerChild: boolean;
  isInSubscription: boolean | null;
  isSection: boolean;
  isSectionReference: boolean;
  titleInAnchor: string;
  type: 'button-group';
  variant: string;
  childOf?: string | null;
  buttonAlignment?: 'left-aligned' | 'right-aligned';
};

const buttonKeys = [
  'primaryLink',
  'firstSecondaryLink',
  'secondSecondaryLink',
  'thirdSecondaryLink',
];

export const buttonGroup = {
  '1p': [buttonKeys[0]],
  '1s': [buttonKeys[1]],
  '1p1s': buttonKeys.slice(0, 2),
  '2s': buttonKeys.slice(1, 3),
  '1p2s': buttonKeys.slice(0, 3),
  '3s': buttonKeys.slice(1, 4),
};

const DEFAULT_BUTTON_VARIANT = '1p';

export function ButtonGroup(props: ButtonGroupProps) {
  const variant = props.variant ?? buttonGroup[DEFAULT_BUTTON_VARIANT];
  const { border, childOf, buttonAlignment = 'left-aligned' } = props;
  const noSectionClosingLine = border === 'disable-border';
  const buttons = useMemo(() => buttonGroup[variant].map((key) => props[key]), [props, variant]);

  return (
    <div className={styles.buttonGroupWrapper}>
      <Container
        className={classNameBuilder(childOf === 'accordion' && ctaButtonStyles.marginIsAccordion)}
        maxContentWidth={buildSize(gridSizes.gddsFullGrid)}
      >
        <div className={classNameBuilder(styles.ctaButtonGroup, styles[buttonAlignment])}>
          {buttons.map((link, i) => (
            <CTAButton
              key={i}
              isGroup
              link={link}
              text={decodingContent(link.text)}
              renderAsLink={false}
              buttonStyle={i === 0 && variant.includes('p') ? 'primary' : 'secondary'}
            />
          ))}
        </div>
      </Container>
      {!noSectionClosingLine && (
        <SectionClosingLine
          withChildAccordionMargin={childOf === 'accordion'}
          withChildTabMargin={childOf === 'tab'}
        />
      )}
    </div>
  );
}
